import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from './config';

interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: 'host' | 'admin';
}

interface UpdateComment {
  id: number;
  user_id: string;
  user_name: string;
  content: string;
  created_at: string;
}

interface UpdateItem {
  id: number;
  title: string;
  content: string;
  admin_name: string;
  created_at: string;
  comment_count: number;
  comments: UpdateComment[];
}

interface Props {
  token: string;
  user: AuthUser | null;
}

function maskName(name: string): string {
  if (name.length <= 1) return name;
  if (name.length === 2) return name[0] + 'O';
  return name[0] + 'O'.repeat(name.length - 2) + name[name.length - 1];
}

function fmtDate(iso: string) {
  if (!iso) return '';
  const utc = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(utc);
  return d.toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' });
}

export default function UpdateBoard({ token, user }: Props) {
  const [updates, setUpdates] = useState<UpdateItem[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [commentInputs, setCommentInputs] = useState<Record<number, string>>({});
  const [showWrite, setShowWrite] = useState(false);
  const [writeForm, setWriteForm] = useState({ title: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchUpdates = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/updates`, { headers });
      if (res.ok) setUpdates(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => { fetchUpdates(); }, [fetchUpdates]);

  const handleWrite = async () => {
    if (!writeForm.title.trim() || !writeForm.content.trim()) {
      alert('제목과 내용을 모두 입력하세요'); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/updates`, {
        method: 'POST', headers, body: JSON.stringify(writeForm),
      });
      if (res.ok) {
        setShowWrite(false);
        setWriteForm({ title: '', content: '' });
        fetchUpdates();
      }
    } finally { setSubmitting(false); }
  };

  const handleDeleteUpdate = async (id: number) => {
    if (!window.confirm('이 업데이트를 삭제하시겠습니까?')) return;
    await fetch(`${API_BASE_URL}/api/updates/${id}`, { method: 'DELETE', headers });
    fetchUpdates();
  };

  const handleComment = async (updateId: number) => {
    const content = (commentInputs[updateId] || '').trim();
    if (!content) return;
    const res = await fetch(`${API_BASE_URL}/api/updates/${updateId}/comments`, {
      method: 'POST', headers, body: JSON.stringify({ content }),
    });
    if (res.ok) {
      setCommentInputs((p) => ({ ...p, [updateId]: '' }));
      fetchUpdates();
    }
  };

  const handleDeleteComment = async (updateId: number, commentId: number) => {
    await fetch(`${API_BASE_URL}/api/updates/${updateId}/comments/${commentId}`, {
      method: 'DELETE', headers,
    });
    fetchUpdates();
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>업데이트 노트</h2>
        {user?.role === 'admin' && (
          <button
            onClick={() => setShowWrite(true)}
            style={{
              padding: '7px 14px', background: '#2563eb', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}
          >+ 작성</button>
        )}
      </div>

      {/* 작성 모달 */}
      {showWrite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowWrite(false); }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 'min(92vw, 500px)', boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>업데이트 작성</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>제목</label>
              <input
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e5e9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                value={writeForm.title}
                onChange={(e) => setWriteForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="예: 예약 제한 1년으로 확대"
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 4 }}>내용</label>
              <textarea
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e5e9', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', minHeight: 120, resize: 'vertical' }}
                value={writeForm.content}
                onChange={(e) => setWriteForm((p) => ({ ...p, content: e.target.value }))}
                placeholder="업데이트 내용을 입력하세요..."
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowWrite(false)} style={{ padding: '8px 16px', border: '1px solid #e2e5e9', borderRadius: 8, background: '#f5f5f5', color: '#333', fontSize: 13, cursor: 'pointer' }}>취소</button>
              <button onClick={handleWrite} disabled={submitting} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>등록</button>
            </div>
          </div>
        </div>
      )}

      {/* 목록 */}
      {updates.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '60px 0', fontSize: 14 }}>등록된 업데이트가 없습니다.</div>
      ) : (
        updates.map((u) => (
          <div key={u.id} style={{ background: '#fff', border: '1px solid #e8ecf0', borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
            {/* 업데이트 본문 */}
            <div style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(u.created_at)}</span>
                  <span style={{ fontSize: 11, color: '#cbd5e1' }}>·</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>관리자</span>
                </div>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => handleDeleteUpdate(u.id)}
                    style={{ fontSize: 11, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                  >삭제</button>
                )}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{u.title}</div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{u.content}</div>
            </div>

            {/* 댓글 토글 버튼 */}
            <div
              onClick={() => setExpandedId(expandedId === u.id ? null : u.id)}
              style={{
                padding: '10px 20px', borderTop: '1px solid #f1f5f9',
                display: 'flex', alignItems: 'center', gap: 6,
                cursor: 'pointer', background: '#fafafa', userSelect: 'none',
              }}
            >
              <span style={{ fontSize: 13, color: '#64748b' }}>💬 댓글 {u.comment_count}개</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{expandedId === u.id ? '▲' : '▼'}</span>
            </div>

            {/* 댓글 섹션 */}
            {expandedId === u.id && (
              <div style={{ padding: '12px 20px 16px', borderTop: '1px solid #f1f5f9', background: '#f8fafc' }}>
                {u.comments.map((c) => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#334155', marginRight: 8 }}>{maskName(c.user_name)}</span>
                      <span style={{ fontSize: 12, color: '#475569' }}>{c.content}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 12 }}>
                      <span style={{ fontSize: 11, color: '#cbd5e1' }}>{fmtDate(c.created_at)}</span>
                      {(user?.id === c.user_id || user?.role === 'admin') && (
                        <button
                          onClick={() => handleDeleteComment(u.id, c.id)}
                          style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >삭제</button>
                      )}
                    </div>
                  </div>
                ))}
                {/* 댓글 입력 */}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <input
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff' }}
                    placeholder="댓글을 입력하세요..."
                    value={commentInputs[u.id] || ''}
                    onChange={(e) => setCommentInputs((p) => ({ ...p, [u.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(u.id); } }}
                  />
                  <button
                    onClick={() => handleComment(u.id)}
                    style={{ padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >등록</button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
